import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { ActionContactDto, ActionCostDto, ActionDetail, ContactPickItem, EmailDto } from '@tww/shared';
import { ACTION_STATUS_LABELS, fmtCoords, fmtDate, fmtDateTime, fmtEur, fmtHa, fmtNum, formatAddress, seasonKey, todayIso, totalProgress } from '@tww/shared';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { FilesTab } from '../../ui/FilesTab';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { ErrorState, FactCard, Facts, Note, Panel, Skeleton, StatusPill } from '../../ui/basics';
import { Select, TextArea } from '../../ui/fields';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/Toast';
import { ReviewNote } from '../../ui/ReviewNote';
import { MapsLink } from '../areas/common';
import { ChecklistItemModal } from './ChecklistItemModal';
import { DocumentsPanel } from './DocumentsPanel';
import { EmailPanel } from '../emails/EmailPanel';
import { PollPanel } from '../polls/PollPanel';
import { ActionProgress, ActionStatusPill, DueCell, ROLE_SHORT, cleanSummary, fmtRange } from './common';

const TABS = [
  { key: 'uebersicht', label: 'Übersicht' },
  { key: 'checkliste', label: 'Checkliste' },
  { key: 'protokoll', label: 'Begehungsprotokoll' },
  { key: 'beteiligte', label: 'Beteiligte' },
  { key: 'dateien', label: 'Dateien' },
  { key: 'emails', label: 'E-Mails' },
  { key: 'stammdaten', label: 'Stammdaten' },
  { key: 'kosten', label: 'Kosten' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/** Aktionsakte (Plan 04 Abschnitt 5.5, Prototyp Screen 03) */
export function ActionDetailPage() {
  const { id } = useParams();
  const aid = Number(id);
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = (TABS.find((t) => t.key === sp.get('tab'))?.key ?? 'uebersicht') as TabKey;
  const itemParam = sp.get('punkt');
  const q = useQuery({ queryKey: ['action', aid], queryFn: () => api.get<ActionDetail>(`/actions/${aid}`), enabled: Number.isFinite(aid) });
  const [openItem, setOpenItem] = useState<number | null>(null);
  const [statusModal, setStatusModal] = useState(false);
  const [completeModal, setCompleteModal] = useState(false);
  const [guideModal, setGuideModal] = useState(false);
  useEffect(() => {
    if (itemParam) setOpenItem(Number(itemParam));
  }, [itemParam]);
  const write = can('actions', 'write');
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['action', aid] });
    qc.invalidateQueries({ queryKey: ['actions'] });
    qc.invalidateQueries({ queryKey: ['today'] });
  };
  const complete = useMutation({
    mutationFn: () => api.post<{ applied: string[] }>(`/actions/${aid}/complete`, {}),
    onSuccess: (r) => {
      toast(r.applied.join(' · '));
      setCompleteModal(false);
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  // E-Mail an die Pflanzlotsen: freie E-Mail mit Empfängern anlegen und im Tab „E-Mails“ öffnen
  const guideMail = useMutation({
    mutationFn: (to: string[]) => api.post<EmailDto>('/emails', { entity_type: 'action', entity_id: aid, to }),
    onSuccess: (m) => {
      setGuideModal(false);
      const n = new URLSearchParams(sp);
      n.set('tab', 'emails');
      n.set('mail_id', String(m.id));
      n.delete('punkt');
      setSp(n, { replace: true });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const newVisit = useMutation({
    mutationFn: () => api.post<{ id: number }>(`/areas/${q.data!.area!.id}/site-visits`, { action_id: aid }),
    onSuccess: (v) => nav(`/protokoll/${v.id}`),
    onError: (e: Error) => toast(e.message, 'error'),
  });
  if (q.isLoading) return <Skeleton rows={8} />;
  if (q.isError || !q.data) return <ErrorState error={q.error ?? new Error('Aktion nicht gefunden')} retry={() => q.refetch()} />;
  const a = q.data;
  const hasPartnerTermin = a.checklist.some((i) => i.template_item_key === 'partner_termin');
  const setTab = (t: TabKey) => {
    const n = new URLSearchParams(sp);
    if (t === 'uebersicht') n.delete('tab');
    else n.set('tab', t);
    n.delete('punkt');
    setSp(n, { replace: true });
  };
  const closeItem = () => {
    setOpenItem(null);
    if (sp.has('punkt')) {
      const n = new URLSearchParams(sp);
      n.delete('punkt');
      setSp(n, { replace: true });
    }
  };
  const total = totalProgress(a.progress);
  const item = openItem ? a.checklist.find((i) => i.id === openItem) : null;
  const openByKey = (key: string) => {
    const it = a.checklist.find((i) => i.template_item_key === key);
    if (it) setOpenItem(it.id);
  };
  const frozen = a.status === 'ausgefallen';
  return (
    <>
      <div className="crumb">
        <Link to="/aktionen">Aktionen</Link> › {a.year ? <Link to={`/aktionen?f.season=${seasonKey(a.season_code, a.year)}`}>{a.season}</Link> : <span>{a.season}</span>}
      </div>
      <div className="head">
        <div>
          <h1>
            {a.label}
            {a.archived_at && <span className="pill muted" style={{ marginLeft: 10, verticalAlign: 'middle' }}>archiviert</span>}
          </h1>
          <div className="sub">{[a.code, fmtRange(a), [a.state_label, a.region].filter(Boolean).join(', '), a.type_label].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="actions">
          <ActionStatusPill status={a.status} />
          {!frozen && <ActionProgress progress={a.progress} />}
          {write && (
            <Link className="btn" to={`/aktionen/${a.id}/bearbeiten`}>
              <Icon name="edit" /> Bearbeiten
            </Link>
          )}
          {write && (
            <button className="btn" type="button" onClick={() => setStatusModal(true)}>
              Status setzen
            </button>
          )}
          {write && !a.completed_at && a.status !== 'ausgefallen' && a.date && a.date < todayIso() && (
            <button className="btn primary" type="button" onClick={() => setCompleteModal(true)}>
              <Icon name="check" /> Abschließen
            </button>
          )}
        </div>
      </div>
      {a.status === 'ausgefallen' && <Note kind="bad">Ausgefallen{a.status_reason ? `: ${a.status_reason}` : ''}. Die Checkliste ist eingefroren; über „Status setzen“ kann die Aktion wieder aktiviert werden.</Note>}
      {a.review_needed && <ReviewNote entity="actions" id={a.id} queryKey={['action', aid]} canWrite={write} />}

      <Facts>
        <FactCard label="Bäume" value={a.trees_planned ? fmtNum(a.trees_planned) : '–'} detail={a.hectares != null ? `auf ${fmtHa(a.hectares)} ha` : 'Hektar noch offen'} />
        <FactCard
          label="Teilnehmende"
          value={a.participants_actual ?? a.participants_planned ?? '–'}
          detail={<span className={a.guides.short && a.status !== 'abgeschlossen' ? 'miss' : undefined}>{`Pflanzlotsen: ${a.guides.needed} nötig, ${a.guides.confirmed + a.guides.forester} bestätigt`}</span>}
        />
        <FactCard
          label="Förster"
          value={a.forester ? a.forester.display_name : 'offen'}
          href={a.forester ? `/kontakte/${a.forester.id}` : undefined}
          missing={!a.forester || ((!a.forester_confirmed_at || (hasPartnerTermin && !a.partner_confirmed_at)) && !['abgeschlossen', 'ausgefallen'].includes(a.status))}
          detail={
            a.forester ? (
              hasPartnerTermin ? (
                `Termin: Förster ${a.forester_confirmed_at ? `bestätigt ${fmtDate(a.forester_confirmed_at)}` : 'offen'}, Spendenpartner ${a.partner_confirmed_at ? `bestätigt ${fmtDate(a.partner_confirmed_at)}` : 'offen'}`
              ) : a.forester_confirmed_at ? (
                `Termin bestätigt am ${fmtDate(a.forester_confirmed_at)}`
              ) : (
                'Termin noch nicht bestätigt'
              )
            ) : write ? (
              <Link className="link" to={`/aktionen/${a.id}/bearbeiten`}>
                Förster zuordnen
              </Link>
            ) : (
              'noch nicht zugeordnet'
            )
          }
        />
        <FactCard label="Fläche" value={a.area ? a.area.name : 'offen'} href={a.area ? `/flaechen/${a.area.id}` : undefined} missing={!a.area} detail={a.area_detail ? (fmtCoords(a.area_detail.lat, a.area_detail.lng) || 'ohne Koordinaten') : 'Begehung steht aus'} />
      </Facts>

      <nav className="nav-tabs" aria-label="Aktionsakte">
        {TABS.map((t) => (
          <button key={t.key} type="button" className={tab === t.key ? 'active' : ''} onClick={() => setTab(t.key)}>
            {t.label}
            {t.key === 'checkliste' && !frozen && <span className={`n ${total.late ? 'miss' : ''}`}> {total.done}/{total.total}{total.late ? ` · ${total.late} überfällig` : ''}</span>}
            {t.key === 'protokoll' && !a.site_visit && <span className="n miss"> fehlt</span>}
            {t.key === 'beteiligte' && <span className="n"> {shownContacts(a).length}</span>}
            {t.key === 'kosten' && a.costs.length > 0 && <span className="n"> {a.costs.length}</span>}
          </button>
        ))}
      </nav>

      {tab === 'uebersicht' && <OverviewTab a={a} onOpen={setOpenItem} onOpenKey={openByKey} onGuides={() => setGuideModal(true)} />}
      {tab === 'checkliste' && <ChecklistTab a={a} onOpen={setOpenItem} />}
      {tab === 'protokoll' && (
        <Panel title="Begehungsprotokoll">
          {a.site_visit ? (
            <div className="pb">
              <p>
                Protokoll vom {fmtDate(a.site_visit.visited_at)} · {a.site_visit.status === 'final' ? <StatusPill code="bestaetigt" label="abgeschlossen" /> : <StatusPill code="warn" label="Entwurf" />}
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Link className="btn primary" to={`/protokoll/${a.site_visit.id}`}>
                  <Icon name="doc" /> Protokoll öffnen
                </Link>
                <a className="btn" href={`/api/v1/site-visits/${a.site_visit.id}/print`} target="_blank" rel="noreferrer">
                  Druckansicht
                </a>
              </div>
            </div>
          ) : (
            <div className="pb">
              <p className="mut" style={{ marginTop: 0 }}>Noch kein Protokoll.</p>
              {a.area ? (
                write && (
                  <button type="button" className="btn primary" onClick={() => newVisit.mutate()} disabled={newVisit.isPending} title="Flächendaten, Baumpreis und Checklistenpunkte werden beim Abschluss des Protokolls übernommen">
                    <Icon name="doc" /> Begehungsprotokoll anlegen
                  </button>
                )
              ) : (
                <p className="mut small">
                  Für das Protokoll braucht die Aktion eine Fläche.{' '}
                  {write && (
                    <Link className="link" to={`/aktionen/${a.id}/bearbeiten`}>
                      Fläche zuordnen
                    </Link>
                  )}
                </p>
              )}
            </div>
          )}
        </Panel>
      )}
      {tab === 'beteiligte' && <ContactsTab a={a} onOpen={setOpenItem} onGuides={() => setGuideModal(true)} write={write} />}
      {tab === 'dateien' && <FilesPanel a={a} write={write} onOpenKey={openByKey} />}
      {tab === 'emails' && <EmailPanel entityType="action" entityId={a.id} prepareBase={`/actions/${a.id}`} />}
      {tab === 'stammdaten' && <MasterDataTab a={a} />}
      {tab === 'kosten' && <CostsTab a={a} write={write} />}

      {item && <ChecklistItemModal item={item} groupName={a.groups.find((g) => g.key === item.group_key)?.name ?? item.group_key} groupRole={a.groups.find((g) => g.key === item.group_key)?.responsible_role ?? null} actionDate={a.date} region={a.region} onClose={closeItem} onSaved={closeItem} />}
      {statusModal && <StatusModal a={a} onClose={() => setStatusModal(false)} onDone={() => (setStatusModal(false), invalidate())} />}
      {completeModal && (
        <ConfirmDialog
          title="Aktion abschließen"
          text={
            <>
              Der Aktionstag {fmtDate(a.date)} ist vorbei. Beim Abschluss wird die Fläche als bepflanzt markiert, das Gewerk Qualitätssicherung erscheint und die Monitoring-Termine nach den eingestellten Intervallen werden angelegt (Fläche geht ins Monitoring).
              {total.open > 0 && <div style={{ marginTop: 8 }}>{total.open} Punkte sind noch offen und bleiben in der Checkliste.</div>}
            </>
          }
          confirmLabel="Abschließen"
          onConfirm={() => complete.mutate()}
          onClose={() => setCompleteModal(false)}
          busy={complete.isPending}
        />
      )}
      {guideModal && <GuideModal a={a} onClose={() => setGuideModal(false)} onDone={() => (setGuideModal(false), invalidate())} onEmail={(to) => guideMail.mutate(to)} />}
    </>
  );
}

/* ---------- Übersicht ---------- */
function OverviewTab({ a, onOpen, onOpenKey, onGuides }: { a: ActionDetail; onOpen: (id: number) => void; onOpenKey: (k: string) => void; onGuides: () => void }) {
  const visible = a.progress;
  return (
    <>
      {a.status === 'abgeschlossen' ? (
        <Note kind="good">Abgeschlossen{a.completed_at ? ` am ${fmtDate(a.completed_at)}` : ''}. Offen bleiben Nachweise und Qualitätssicherung.</Note>
      ) : a.status === 'ausgefallen' ? null : a.hints.length ? (
        <div className="note">
          <strong>Das fehlt noch:</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {a.hints.map((h, i) => (
              <li key={i}>
                {h.item_key ? (
                  <button type="button" className="btn link-like" onClick={() => onOpenKey(h.item_key!)}>
                    {h.text}
                  </button>
                ) : (
                  h.text
                )}
                {h.item_key === 'lotsen' && (
                  <button type="button" className="btn sm" style={{ marginLeft: 10 }} onClick={onGuides}>
                    Pflanzlotsen in der Region suchen
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <Note kind="good">Alle Zusagen liegen vor: Fläche, Förster mit bestätigtem Termin, Pflanzlotsen und Projektvereinbarung.</Note>
      )}
      <div className="grid g3 gewerke">
        {visible.map((g) => {
          const role = g.responsible_role ? ROLE_SHORT[g.responsible_role] : null;
          return (
          <Panel key={g.key} title={<>{g.name}{role && role !== g.name && <span className="mut" style={{ fontWeight: 400 }}> · {role}</span>}</>} action={<span className={`small cnt ${g.late ? 'miss' : 'mut'}`}>{g.done} von {g.total}</span>}>
            <div className="pb">
              <div className="bar">
                <i style={{ width: `${g.total ? Math.round((100 * g.done) / g.total) : 0}%` }} className={g.late ? 'late' : g.done === g.total ? 'full' : ''} />
              </div>
              {g.open.length === 0 ? (
                <div className="mut small" style={{ marginTop: 8 }}>
                  alles erledigt
                </div>
              ) : (
                <ul className="open-list">
                  {g.open.slice(0, 3).map((o) => (
                    <li key={o.id}>
                      <button type="button" className="btn link-like" onClick={() => onOpen(o.id)}>
                        {o.title}
                      </button>
                      <span className={`small ${o.late ? 'miss' : 'mut'}`}>{o.due ? (o.late ? `überfällig seit ${fmtDate(o.due).slice(0, 6)}` : `bis ${fmtDate(o.due).slice(0, 6)}`) : 'kein Termin'}</span>
                    </li>
                  ))}
                  {g.open.length > 3 && <li className="mut small">+ {g.open.length - 3} weitere</li>}
                </ul>
              )}
            </div>
          </Panel>
          );
        })}
      </div>
    </>
  );
}

/* ---------- Checkliste ---------- */
function ChecklistTab({ a, onOpen }: { a: ActionDetail; onOpen: (id: number) => void }) {
  const past = !!a.date && a.date < todayIso();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => Object.fromEntries(a.groups.map((g) => [g.key, true])));
  return (
    <div className="col">
      {a.groups
        .filter((g) => !g.after_action || past)
        .map((g) => {
          const items = a.checklist.filter((i) => i.group_key === g.key);
          const p = a.progress.find((x) => x.key === g.key);
          const open = openGroups[g.key] ?? true;
          return (
            <div className="panel" key={g.key}>
              <button type="button" className="ph acc" onClick={() => setOpenGroups((s) => ({ ...s, [g.key]: !open }))} aria-expanded={open}>
                <span style={{ display: 'flex', gap: 12, alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <Icon name="chev" className={open ? 'rot' : ''} />
                  <h2>{g.name}</h2>
                  {g.responsible_role && ROLE_SHORT[g.responsible_role] !== g.name && <span className="mut small">zuständig {ROLE_SHORT[g.responsible_role]}</span>}
                </span>
                {p && (
                  <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span className="bar" style={{ width: 120 }}>
                      <i style={{ width: `${p.total ? Math.round((100 * p.done) / p.total) : 0}%` }} className={p.late ? 'late' : p.done === p.total ? 'full' : ''} />
                    </span>
                    <span className={`small ${p.late ? 'miss' : 'mut'}`}>
                      {p.done}/{p.total}
                      {p.late ? ` · ${p.late} überfällig` : ''}
                    </span>
                  </span>
                )}
              </button>
              {open && (
                <ul className="cl-list">
                  {items.map((i) => {
                    const summary = cleanSummary(i.summary);
                    return (
                    <li key={i.id} className={`cl-item ${i.state}`} onClick={() => onOpen(i.id)}>
                      <span className={`box ${i.state}`} aria-label={i.state === 'done' ? 'erledigt' : i.state === 'not_needed' ? 'nicht benötigt' : 'offen'}>
                        {i.state === 'done' ? <Icon name="check" /> : i.state === 'not_needed' ? '–' : ''}
                      </span>
                      <span className="t">
                        <span className="title">
                          {i.title}
                          {i.optional && <span className="mut small"> · optional</span>}
                        </span>
                        {summary && <span className="sm">{summary}</span>}
                      </span>
                      <span className="right">
                        <DueCell item={i} />
                        {(i.responsible_user_name || i.responsible_role) && <span className="sm">{i.responsible_user_name ?? ROLE_SHORT[i.responsible_role!]}</span>}
                      </span>
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      {a.own_due_count > 0 && <div className="mut small">{a.own_due_count} eigene Fristen werden bei Terminänderungen nicht angepasst.</div>}
    </div>
  );
}

/* ---------- Beteiligte ---------- */
const CONTACT_STATUS_PILL: Record<string, string> = { geplant: 'muted', angefragt: 'abstimmung', bestaetigt: 'bestaetigt', gebucht: 'bestaetigt', abgesagt: 'offen' };
/** Abgesagte bleiben nur bei Pflanzlotsen sichtbar; gilt für Liste und Zähler im Tab */
const shownContacts = (a: ActionDetail) => a.contacts.filter((c) => c.status !== 'abgesagt' || c.role === 'guide');
/** Beteiligte: Terminabstimmung über der Liste, solange sie aussteht, danach darunter */
function ContactsTab(props: { a: ActionDetail; onOpen: (id: number) => void; onGuides: () => void; write: boolean }) {
  return (
    <>
      <PollPanel a={props.a} place="top" />
      <ContactsList {...props} />
      <PollPanel a={props.a} place="bottom" />
    </>
  );
}
function ContactsList({ a, onOpen, onGuides, write }: { a: ActionDetail; onOpen: (id: number) => void; onGuides: () => void; write: boolean }) {
  const list = shownContacts(a);
  return (
    <Panel
      title="Beteiligte"
      action={
        <span style={{ display: 'flex', gap: 8 }}>
          {write && (
            <Link className="btn sm" to={`/aktionen/${a.id}/beteiligte/neu`}>
              <Icon name="plus" /> Beteiligung
            </Link>
          )}
          {write && a.status !== 'abgeschlossen' && (
            <button type="button" className="btn sm primary" onClick={onGuides}>
              Pflanzlotsen in der Region suchen
            </button>
          )}
        </span>
      }
    >
      {list.length === 0 ? (
        <div className="empty">Noch keine Beteiligten</div>
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Rolle</th>
                <th>Name</th>
                <th>Stand</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id}>
                  <td>{c.role_label}</td>
                  <td>
                    <Link className="link" to={`/kontakte/${c.contact_id}`}>
                      {c.display_name}
                    </Link>
                    <span className="sm">{[c.organisation_name, c.mobile ?? c.phone, c.email].filter(Boolean).join(' · ')}</span>
                  </td>
                  <td>
                    <StatusPill code={CONTACT_STATUS_PILL[c.status] ?? 'muted'} label={contactStatusText(c)} />
                  </td>
                  <td className="num">
                    {c.item_id && (
                      <button type="button" className="btn sm" onClick={() => onOpen(c.item_id!)}>
                        {c.item_title ?? 'Punkt'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
function contactStatusText(c: ActionContactDto): string {
  if (c.role === 'forester') return c.confirmed_at ? `Termin bestätigt ${fmtDate(c.confirmed_at)}` : c.status_label;
  if (c.role === 'partner') return 'Auftrag';
  if (c.status === 'gebucht') return `${['nursery', 'fence', 'caterer', 'wc', 'bus', 'supplier_other'].includes(c.role) ? 'bestellt' : 'gebucht'}${c.confirmed_at ? ' ' + fmtDate(c.confirmed_at) : ''}`;
  if (c.status === 'bestaetigt') return `bestätigt${c.confirmed_at ? ' ' + fmtDate(c.confirmed_at) : ''}`;
  if (c.status === 'angefragt') return `${c.requested_at && ['nursery', 'fence', 'caterer', 'wc', 'bus', 'supplier_other'].includes(c.role) ? 'Angebot liegt vor' : 'angefragt'}${c.requested_at ? ' ' + fmtDate(c.requested_at) : ''}`;
  return c.status_label;
}
/** Pflanzlotsen in der Region suchen und anfragen (Prototyp) */
function GuideModal({ a, onClose, onDone, onEmail }: { a: ActionDetail; onClose: () => void; onDone: () => void; onEmail: (to: string[]) => void }) {
  const toast = useToast();
  const guideMails = a.contacts.filter((c) => c.role === 'guide' && c.status !== 'abgesagt' && c.email).map((c) => formatAddress(c.display_name, c.email!));
  const q = useQuery({ queryKey: ['suggest-guides', a.id], queryFn: () => api.get<{ items: (ContactPickItem & { in_region: boolean; in_state: boolean })[] }>(`/actions/${a.id}/suggest-guides`).then((r) => r.items) });
  const [sel, setSel] = useState<number[]>([]);
  const m = useMutation({
    mutationFn: () => api.post(`/actions/${a.id}/contacts/request`, { contact_ids: sel, role: 'guide' }),
    onSuccess: () => (toast(`${sel.length} Pflanzlotsen angefragt`), onDone()),
    onError: (e: Error) => toast(e.message, 'error'),
  });
  return (
    <Modal
      title="Pflanzlotsen in der Region"
      subtitle={`${a.region ?? a.state_label ?? 'ohne Region'} · Bedarf ${a.guides.needed}, davon ${a.guides.tww} von TWW`}
      onClose={onClose}
      footer={
        <>
          {guideMails.length > 0 && (
            <button className="btn" type="button" style={{ marginRight: 'auto' }} onClick={() => onEmail(guideMails)}>
              <Icon name="mail" /> E-Mail an Lotsen
            </button>
          )}
          <button className="btn" type="button" onClick={onClose}>Abbrechen</button>
          <button className="btn primary" type="button" disabled={!sel.length || m.isPending} onClick={() => m.mutate()} title="Setzt die Beteiligung auf „angefragt“ und trägt das Datum im Punkt „Pflanzlotsen“ ein">
            {sel.length ? `${sel.length} anfragen` : 'Anfragen'}
          </button>
        </>
      }
    >
      {q.isLoading ? (
        <Skeleton rows={4} />
      ) : (q.data ?? []).length === 0 ? (
        <div className="empty">Keine weiteren Pflanzlotsen in Region oder Bundesland. Neue Lotsen unter Kontakte anlegen.</div>
      ) : (
        <ul className="check-list no-date one">
          {q.data!.map((c) => (
            <li key={c.id} className={sel.includes(c.id) ? 'on' : ''}>
              <label>
                <input type="checkbox" checked={sel.includes(c.id)} onChange={(e) => setSel((s) => (e.target.checked ? [...s, c.id] : s.filter((x) => x !== c.id)))} />
                <span>
                  {c.display_name}
                  <span className="sub">{c.in_region ? c.region : `${c.region ?? ''} (Bundesland)`}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/* ---------- Dateien ---------- */
function FilesPanel({ a, write, onOpenKey }: { a: ActionDetail; write: boolean; onOpenKey: (k: string) => void }) {
  return (
    <div className="col">
      <DocumentsPanel a={a} onOpenItem={onOpenKey} />
      <div className="panel">
        <FilesTab entityType="action" entityId={a.id} canWrite={write} ownLabel="Dateien dieser Aktion" />
      </div>
    </div>
  );
}

/* ---------- Stammdaten ---------- */
function MasterDataTab({ a }: { a: ActionDetail }) {
  const dl = (rows: [string, React.ReactNode][]) => (
    <dl className="dl">
      {rows.map(([k, v]) => (
        <FragmentRow key={k} k={k} v={v} />
      ))}
    </dl>
  );
  return (
    <div className="grid g2">
      <Panel title="Kennung und Termin">
        <div className="pb">{dl([['Kennung', a.code], ['Typ', a.type_label], ['Saison', a.season], ['Aktionstag', fmtRange(a)], ['Uhrzeit', [a.time_start, a.time_end].filter(Boolean).join(' bis ') || '–'], ['Status', `${ACTION_STATUS_LABELS[a.status]}${a.status_manual ? ' (manuell)' : ''}`], ['Projektleitung', a.project_lead?.display_name ?? '–'], ['Team', a.team_initials ?? '–']])}</div>
      </Panel>
      <Panel title="Partner und Ort">
        <div className="pb">{dl([['Spendenpartner', a.partner ? <Link className="link" to={`/kontakte/${a.partner.id}`}>{a.partner.display_name}</Link> : '–'], ['Förster', a.forester ? <Link className="link" to={`/kontakte/${a.forester.id}`}>{a.forester.display_name}</Link> : '–'], ['Waldbesitzer', a.owner ? <Link className="link" to={`/kontakte/${a.owner.id}`}>{a.owner.display_name}</Link> : '–'], ['Bundesland', a.state_label ?? '–'], ['Region', a.region ?? '–'], ['Ort', a.city ?? '–']])}</div>
      </Panel>
      <Panel title="Fläche und Bäume" action={a.area_detail ? <MapsLink lat={a.area_detail.lat} lng={a.area_detail.lng} /> : undefined}>
        <div className="pb">{dl([['Fläche', a.area ? <Link className="link" to={`/flaechen/${a.area.id}`}>{a.area.name}</Link> : '–'], ['Hektar', fmtHa(a.hectares)], ['Bäume geplant', fmtNum(a.trees_planned)], ['Bäume berechnet', fmtNum(a.trees_calculated)], ['durch Teilnehmende', fmtNum(a.trees_by_participants)], ['Baumarten', a.species.length ? a.species.map((s) => `${s.label} ${fmtNum(s.count)}`).join(', ') : a.species_text ?? '–']])}</div>
      </Panel>
      <Panel title="Teilnehmende">
        <div className="pb">{dl([['geplant', `${a.participants_planned ?? '–'} (davon Kinder ${a.children_planned ?? '–'})`], ['real', `${a.participants_actual ?? '–'} (davon Kinder ${a.children_actual ?? '–'})`], ['Pflanzlotsen', `${a.guides.needed} nötig, ${a.guides.tww} von TWW, ${a.guides.confirmed} bestätigt, ${a.guides.forester} vom Förster`]])}</div>
      </Panel>
      <Panel title="Finanzen">
        <div className="pb">{dl([['Spendenzusage', fmtEur(a.donation_pledged)], ['Eingang Vorjahr', fmtEur(a.donation_received_prev_year)], ['Eingang aktuelles Jahr', fmtEur(a.donation_received_this_year)], ['Eingang am', fmtDate(a.donation_received_at)], ['Baumpreis', a.price_per_tree == null ? '–' : `${fmtEur(a.price_per_tree)} ${a.price_basis}${a.vat_rate != null ? ` (${fmtNum(a.vat_rate)} %: netto ${fmtEur(a.price_net)}, brutto ${fmtEur(a.price_gross)})` : ''}`], ['Kulturkosten berechnet (brutto)', fmtEur(a.culture_costs_calc)], ['Kulturkosten erwartet', fmtEur(a.culture_costs_expected)], ['Vereinbarungen', a.agreements_note ?? '–']])}</div>
      </Panel>
      <Panel title="Nachbereitung">
        <div className="pb">{dl([['Wirkungsbericht raus', fmtDate(a.impact_report_sent_at)], ['Spendenbescheinigung raus', fmtDate(a.donation_receipt_sent_at)], ['Nistkästen', a.nest_boxes ?? '–'], ['Pressespiegel', a.press_review ? 'vorhanden' : '–'], ['Mietwagen', a.rental_car ?? '–'], ['Kommentar Pflanzung', a.planting_comment ?? '–'], ['Vorbereitungen', a.preparations_note ?? '–'], ['Notizen', a.notes ?? '–'], ['Angelegt', fmtDateTime(a.created_at)]])}</div>
      </Panel>
    </div>
  );
}
function FragmentRow({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </>
  );
}

/* ---------- Kosten ---------- */
function CostsTab({ a, write }: { a: ActionDetail; write: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const [confirmDel, setConfirmDel] = useState<ActionCostDto | null>(null);
  const del = useMutation({
    mutationFn: (id: number) => api.delete(`/actions/${a.id}/costs/${id}`),
    onSuccess: () => (qc.invalidateQueries({ queryKey: ['action', a.id] }), toast('Rechnung gelöscht'), setConfirmDel(null)),
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const byCat = useMemo(() => {
    const m = new Map<string, { label: string; sum: number; rows: ActionCostDto[] }>();
    for (const c of a.costs) {
      const e = m.get(c.category_code) ?? { label: c.category_label, sum: 0, rows: [] };
      e.sum += c.amount;
      e.rows.push(c);
      m.set(c.category_code, e);
    }
    return [...m.values()];
  }, [a.costs]);
  const trees = a.trees_calculated || a.trees_planned;
  return (
    <div className="grid g21">
      <Panel
        title="Rechnungen je Kategorie"
        action={
          write && (
            <Link className="btn sm primary" to={`/aktionen/${a.id}/rechnung/neu`}>
              <Icon name="plus" /> Rechnung
            </Link>
          )
        }
      >
        {a.costs.length === 0 ? (
          <div className="empty">Noch keine Rechnungen</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Kategorie / Rechnung</th>
                  <th>Datum</th>
                  <th>Lieferant</th>
                  <th className="num">Betrag</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {byCat.map((g) => (
                  <GroupCost key={g.label} g={g} write={write} onEdit={(c) => nav(`/aktionen/${a.id}/rechnung/${c.id}`)} onDelete={setConfirmDel} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <div className="col">
        <Panel title="Summe">
          <div className="pb">
            <dl className="dl">
              <dt>Gesamt</dt>
              <dd>
                <strong>{fmtEur(a.cost_total)}</strong>
              </dd>
              <dt>davon Forst</dt>
              <dd>{fmtEur(a.cost_forest)}</dd>
              <dt>davon Aktion</dt>
              <dd title="Fahrt, Verpflegung, Übernachtung und Sonstiges">{fmtEur(a.cost_other)}</dd>
              <dt>Forstkosten je Baum</dt>
              <dd>{a.cost_per_tree != null ? `${fmtEur(a.cost_per_tree)} (${fmtNum(trees)} Bäume)` : '–'}</dd>
              <dt>Spendenzusage</dt>
              <dd>{fmtEur(a.donation_pledged)}</dd>
              <dt>Kulturkosten erwartet (brutto)</dt>
              <dd>{fmtEur(a.culture_costs_expected ?? a.culture_costs_calc)}</dd>
              {a.donation_pledged != null && a.cost_total > 0 && (
                <>
                  <dt>Überschuss</dt>
                  <dd className={a.cost_total > a.donation_pledged ? 'miss' : 'ok'}>{fmtEur(a.donation_pledged - a.cost_total)}</dd>
                </>
              )}
            </dl>
          </div>
        </Panel>
      </div>
      {confirmDel && (
        <ConfirmDialog
          title="Rechnung löschen"
          text={`Rechnung ${confirmDel.invoice_no ?? 'ohne Nummer'} über ${fmtEur(confirmDel.amount)} löschen?`}
          confirmLabel="Löschen"
          danger
          onConfirm={() => del.mutate(confirmDel.id)}
          onClose={() => setConfirmDel(null)}
          busy={del.isPending}
        />
      )}
    </div>
  );
}
function GroupCost({ g, write, onEdit, onDelete }: { g: { label: string; sum: number; rows: ActionCostDto[] }; write: boolean; onEdit: (c: ActionCostDto) => void; onDelete: (c: ActionCostDto) => void }) {
  return (
    <>
      <tr className="group">
        <td colSpan={3}>{g.label}</td>
        <td className="num">{fmtEur(g.sum)}</td>
        <td></td>
      </tr>
      {g.rows.map((c) => (
        <tr key={c.id}>
          <td>
            {c.invoice_no ?? <span className="mut">ohne Nummer</span>}
            {c.note && <span className="sm">{c.note}</span>}
          </td>
          <td>{fmtDate(c.invoice_date)}</td>
          <td>{c.supplier ? <Link className="link" to={`/kontakte/${c.supplier.id}`}>{c.supplier.display_name}</Link> : '–'}</td>
          <td className="num">{fmtEur(c.amount)}</td>
          <td className="num">
            {write && (
              <span style={{ display: 'inline-flex', gap: 2 }}>
                <button type="button" className="icon-btn" aria-label="Bearbeiten" onClick={() => onEdit(c)}>
                  <Icon name="edit" />
                </button>
                <button type="button" className="icon-btn" aria-label="Entfernen" onClick={() => onDelete(c)}>
                  <Icon name="trash" />
                </button>
              </span>
            )}
          </td>
        </tr>
      ))}
    </>
  );
}

/* ---------- Status ---------- */
function StatusModal({ a, onClose, onDone }: { a: ActionDetail; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [status, setStatus] = useState<'' | 'ausgefallen' | 'abgeschlossen' | 'auto'>('');
  const [reason, setReason] = useState(a.status_reason ?? '');
  const m = useMutation({
    mutationFn: () => api.post<ActionDetail & { applied?: string[] }>(`/actions/${a.id}/status`, { status, reason: reason || null }),
    onSuccess: (r) => (toast(r.applied?.length ? r.applied.join(' · ') : 'Status gesetzt'), onDone()),
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const options = [
    ...(a.status !== 'ausgefallen' && !a.completed_at ? [{ value: 'ausgefallen', label: 'ausgefallen' }] : []),
    ...(a.status !== 'ausgefallen' && !a.completed_at ? [{ value: 'abgeschlossen', label: 'abgeschlossen' }] : []),
    ...(a.status_manual ? [{ value: 'auto', label: 'automatisch aus den Daten' }] : []),
  ];
  const confirmTitle = status === 'abgeschlossen' ? 'Fläche wird als bepflanzt markiert, Monitoring-Termine werden angelegt' : status === 'ausgefallen' ? 'Die Checkliste wird eingefroren' : undefined;
  return (
    <Modal
      title="Status setzen"
      subtitle={`aktuell ${ACTION_STATUS_LABELS[a.status]}${a.status_manual ? ' (manuell)' : ' (automatisch)'}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Abbrechen</button>
          <button className="btn primary" type="button" disabled={!status || m.isPending || (status === 'ausgefallen' && !reason.trim())} onClick={() => m.mutate()} title={confirmTitle}>
            Übernehmen
          </button>
        </>
      }
    >
      <div className="form one">
        <Select label="Neuer Status" value={status} onChange={(v) => setStatus(v as typeof status)} options={options} placeholder="wählen" />
        {(status === 'ausgefallen' || status === 'abgeschlossen') && <TextArea label={status === 'ausgefallen' ? 'Grund' : 'Grund (optional)'} required={status === 'ausgefallen'} value={reason} onChange={setReason} rows={3} />}
      </div>
    </Modal>
  );
}
